import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Trash2 } from 'lucide-react';

const API = 'https://api.glowytics.com/api/tiktok-radar';

/* ── Helpers ───────────────────────────────────────────────────────── */

const format = (n) => Number(n || 0).toLocaleString('en-US');
const formatNumber = (n) => {
  if (n === null || n === undefined) return '-';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return Number(n).toLocaleString('en-US');
};
const formatDate = (ts) => {
  if (!ts) return '-';
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
};
const formatShortDate = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
};
const timeAgo = (ts) => {
  if (!ts) return '';
  const sec = typeof ts === 'number' ? ts : Math.floor(new Date(ts).getTime() / 1000);
  const diff = Math.floor(Date.now() / 1000) - sec;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return `${Math.floor(diff / 2592000)}mo ago`;
};
const formatDuration = (sec) => {
  if (!sec) return '-';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};
const getBrandId = () => {
  try { const c = localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId'); if (c) return c; } catch (_) {}
  for (const k of ['brandId', 'selectedBrandId', 'activeBrandId', 'brand_id']) { try { const v = localStorage.getItem(k) || sessionStorage.getItem(k); if (v) return v; } catch (_) {} }
  for (const k of ['selectedBrand', 'currentBrand', 'activeBrand']) { try { const r = localStorage.getItem(k) || sessionStorage.getItem(k); if (r) { const o = JSON.parse(r); if (o?.id) return o.id; } } catch (_) {} }
  try { const m = window.location.pathname.match(/[0-9a-fA-F-]{36}/); if (m) return m[0]; } catch (_) {}
  return '';
};

/* ── Analytics ─────────────────────────────────────────────────────── */

function getEngagementGrade(rate) {
  if (rate == null) return { grade: '?', color: 'text-slate-400', bg: 'bg-slate-50', border: 'border-slate-200', label: 'N/A' };
  if (rate >= 8)   return { grade: 'A+', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Exceptional' };
  if (rate >= 5)   return { grade: 'A', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Excellent' };
  if (rate >= 3)   return { grade: 'B+', color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-200', label: 'Very Good' };
  if (rate >= 2)   return { grade: 'B', color: 'text-sky-600', bg: 'bg-sky-50', border: 'border-sky-200', label: 'Good' };
  if (rate >= 1)   return { grade: 'C+', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Average' };
  if (rate >= 0.5) return { grade: 'C', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Below Avg' };
  if (rate >= 0.2) return { grade: 'D', color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200', label: 'Poor' };
  return { grade: 'F', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Very Poor' };
}

function getPostingFrequency(videos) {
  if (videos.length < 2) return { perWeek: 0, perMonth: 0 };
  const dates = videos.map(p => {
    if (!p.posted_at) return 0;
    return typeof p.posted_at === 'number' ? p.posted_at : Math.floor(new Date(p.posted_at).getTime() / 1000);
  }).filter(Boolean).sort((a, b) => a - b);
  if (dates.length < 2) return { perWeek: 0, perMonth: 0 };
  const spanDays = (dates[dates.length - 1] - dates[0]) / 86400;
  if (spanDays < 1) return { perWeek: videos.length, perMonth: videos.length };
  const perDay = dates.length / spanDays;
  return { perWeek: Math.round(perDay * 7 * 10) / 10, perMonth: Math.round(perDay * 30) };
}

function getBestPostingHours(videos) {
  const hours = Array.from({ length: 24 }, (_, i) => ({ hour: i, count: 0, totalEng: 0, avgEng: 0 }));
  for (const v of videos) {
    if (!v.posted_at) continue;
    const ts = typeof v.posted_at === 'number' ? v.posted_at : Math.floor(new Date(v.posted_at).getTime() / 1000);
    const h = new Date(ts * 1000).getHours();
    hours[h].count++;
    hours[h].totalEng += (Number(v.like_count) || 0) + (Number(v.comment_count) || 0) + (Number(v.share_count) || 0);
  }
  hours.forEach(h => { h.avgEng = h.count > 0 ? Math.round(h.totalEng / h.count) : 0; });
  return hours;
}

function getTopVideos(videos, n = 6) {
  return [...videos].sort((a, b) => (Number(b.play_count) || 0) - (Number(a.play_count) || 0)).slice(0, n);
}

function computeDailyChanges(dailyStats) {
  if (!dailyStats || dailyStats.length === 0) return [];
  const sorted = [...dailyStats].sort((a, b) => new Date(a.stat_date) - new Date(b.stat_date));
  return sorted.map((s, i) => {
    const prev = i > 0 ? sorted[i - 1] : null;
    const fc = Number(s.follower_count) || 0, fwc = Number(s.following_count) || 0, vc = Number(s.video_count) || 0, hc = Number(s.heart_count) || 0;
    return {
      date: s.stat_date, followers: fc, following: fwc, videos: vc, hearts: hc,
      followerChange: prev ? fc - (Number(prev.follower_count) || 0) : 0,
      heartChange: prev ? hc - (Number(prev.heart_count) || 0) : 0,
    };
  }).reverse();
}

/* ── Sub-components ────────────────────────────────────────────────── */

const TikTokIcon = ({ className = 'w-7 h-7' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.48c1.77-1.78 2.12-3.34 2.12-5.09V9.15a8.16 8.16 0 004.74 1.52v-3.4a4.85 4.85 0 01-1.28-.58z"/>
  </svg>
);

function MiniLineChart({ data, height = 80, color = '#0ea5e9' }) {
  if (!data || data.length < 2) return <div className="h-20 flex items-center justify-center text-xs text-slate-400">Not enough data</div>;
  const W = 400, H = height;
  const values = data.map(d => d.value);
  const mn = Math.min(...values), mx = Math.max(...values), range = mx - mn || 1;
  const points = data.map((d, i) => ({ x: (i / (data.length - 1)) * W, y: H - 5 - ((d.value - mn) / range) * (H - 15) }));
  const line = points.map(p => `${p.x},${p.y}`).join(' ');
  const area = `0,${H} ${line} ${W},${H}`;
  const last = points[points.length - 1];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" style={{ height }}>
      <defs><linearGradient id="tcg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity="0.12" /><stop offset="100%" stopColor={color} stopOpacity="0.01" /></linearGradient></defs>
      <polygon points={area} fill="url(#tcg)" />
      <polyline points={line} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last.x} cy={last.y} r="3" fill={color} />
    </svg>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="w-0.5 h-4 rounded-full bg-slate-300" />
      <span className="text-[13px] font-semibold text-slate-800 tracking-tight">{children}</span>
    </div>
  );
}

function PostingHoursChart({ hours }) {
  const maxEng = Math.max(...hours.map(h => h.avgEng), 1);
  if (!hours.some(h => h.count > 0)) return <div className="text-xs text-slate-400 text-center py-4">No posting data</div>;
  return (
    <div>
      <div className="flex items-end gap-px h-16 mb-1">
        {hours.map(h => (
          <div key={h.hour} className="flex-1 group relative">
            <div className="w-full rounded-t-sm" style={{ height: h.count > 0 ? `${Math.max(12, (h.avgEng / maxEng) * 100)}%` : '0', backgroundColor: h.count > 0 ? `rgba(14,165,233,${0.2 + (h.avgEng / maxEng) * 0.6})` : 'transparent' }} />
            {h.count > 0 && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-[9px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                {String(h.hour).padStart(2, '0')}:00 · {h.count} videos · avg {formatNumber(h.avgEng)} eng
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-px text-[8px] text-slate-400">
        {hours.map(h => <div key={h.hour} className="flex-1 text-center">{h.hour % 4 === 0 ? String(h.hour).padStart(2, '0') : ''}</div>)}
      </div>
    </div>
  );
}

/* ── Main ──────────────────────────────────────────────────────────── */

export default function TiktokRadarLab() {
  const [accounts, setAccounts] = useState([]);
  const [query, setQuery] = useState('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [deleteState, setDeleteState] = useState({ open: false, step: 1, account: null });
  const [selectedId, setSelectedId] = useState(null);
  const [profileData, setProfileData] = useState({});
  const [profileLoading, setProfileLoading] = useState({});
  const [viewMode, setViewMode] = useState('grid');
  const [showAllDays, setShowAllDays] = useState(false);
  const previewRef = useRef(null);
  const [videoPopup, setVideoPopup] = useState(null);
  const searchTimer = useRef(null);

  const loadAccounts = async () => {
    try {
      const brandId = getBrandId(); if (!brandId) return;
      const resp = await fetch(`${API}/accounts`, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' });
      if (!resp.ok) return;
      const data = await resp.json();
      setAccounts(data?.accounts || []);
    } catch (_) {}
  };
  useEffect(() => { loadAccounts(); }, []);

  const filtered = accounts.filter(a =>
    (a.nickname || '').toLowerCase().includes(query.toLowerCase()) ||
    (a.unique_id || '').toLowerCase().includes(query.toLowerCase())
  );

  // Debounced search
  const doSearch = async (kw) => {
    if (!kw || kw.trim().length < 2) { setSearchResults([]); return; }
    setIsSearching(true);
    try {
      const resp = await fetch(`${API}/search?keyword=${encodeURIComponent(kw.trim())}`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setSearchResults(data?.users || []);
      }
    } catch (_) {}
    finally { setIsSearching(false); }
  };

  const onSearchChange = (val) => {
    setSearchKeyword(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => doSearch(val), 500);
  };

  const addAccount = async (uniqueId) => {
    setErrorMsg('');
    if (!uniqueId) { setErrorMsg('Please enter a username'); return; }
    const brandId = getBrandId();
    if (!brandId) { setErrorMsg('Brand not selected'); return; }
    setIsSaving(true);
    try {
      const resp = await fetch(`${API}/account/add`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId }, credentials: 'include', body: JSON.stringify({ uniqueId, brandId }) });
      const data = await resp.json();
      if (!resp.ok || !data?.ok) throw new Error(data?.error || 'Failed');
      await loadAccounts(); setSearchKeyword(''); setSearchResults([]); setIsAddOpen(false);
    } catch (e) { setErrorMsg(e?.message || 'Failed to add account'); }
    finally { setIsSaving(false); }
  };

  const requestDelete = (account) => setDeleteState({ open: true, step: 1, account });
  const performDelete = async () => {
    if (!deleteState?.account) return;
    const brandId = getBrandId(); if (!brandId) return;
    try {
      setIsSaving(true);
      const resp = await fetch(`${API}/account/${deleteState.account.id}`, { method: 'DELETE', headers: { 'X-Brand-ID': brandId }, credentials: 'include' });
      if (!resp.ok) throw new Error('Delete failed');
      setAccounts(prev => prev.filter(a => a.id !== deleteState.account.id));
      if (selectedId === deleteState.account.id) setSelectedId(null);
      setDeleteState({ open: false, step: 1, account: null });
    } catch (e) { setErrorMsg(e?.message || 'Delete failed'); }
    finally { setIsSaving(false); }
  };

  const selectAccount = (account) => {
    const newId = selectedId === account.id ? null : account.id;
    setSelectedId(newId); setShowAllDays(false);
    if (newId) { fetchProfileForAccount(newId, account); setTimeout(() => previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100); }
  };

  const fetchProfileForAccount = async (accountId, account) => {
    if (profileData[accountId]) return;
    const brandId = getBrandId();
    const dbProfile = {
      unique_id: account.unique_id, nickname: account.nickname, signature: account.signature || null,
      follower_count: Number(account.follower_count) || 0, following_count: Number(account.following_count) || 0,
      video_count: Number(account.video_count) || 0, heart_count: Number(account.heart_count) || 0,
      is_verified: account.is_verified, avatar_url: account.avatar_url, bio_link: account.bio_link || null,
    };
    setProfileData(prev => ({ ...prev, [accountId]: { profile: dbProfile, videos: [], stats: null, source: 'db' } }));
    setProfileLoading(prev => ({ ...prev, [accountId]: true }));
    try {
      const [postsResp, growthResp, dailyResp] = await Promise.all([
        fetch(`${API}/account/${accountId}/posts`, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' }),
        fetch(`${API}/account/${accountId}/growth`, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' }),
        fetch(`${API}/account/${accountId}/daily-stats`, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' }),
      ]);
      let growth = null;
      if (growthResp.ok) { const g = await growthResp.json(); if (g?.ok) growth = g; }
      let dailyStats = [];
      if (dailyResp.ok) { const d = await dailyResp.json(); if (d?.ok) dailyStats = d.stats || []; }
      if (postsResp.ok) {
        const data = await postsResp.json();
        const videos = (data?.videos || []).map(v => ({
          tiktok_video_id: v.tiktok_video_id,
          caption: v.caption, video_duration: v.video_duration ? Number(v.video_duration) : null,
          cover_url: v.cover_url, video_url: v.video_url || null,
          play_count: Number(v.play_count) || 0, like_count: Number(v.like_count) || 0,
          comment_count: Number(v.comment_count) || 0, share_count: Number(v.share_count) || 0,
          collect_count: Number(v.collect_count) || 0,
          music_title: v.music_title, music_author: v.music_author,
          hashtags: v.hashtags || [], posted_at: v.posted_at,
        }));
        const totalPlays = videos.reduce((s, v) => s + (v.play_count || 0), 0);
        const totalLikes = videos.reduce((s, v) => s + (v.like_count || 0), 0);
        const totalComments = videos.reduce((s, v) => s + (v.comment_count || 0), 0);
        const totalShares = videos.reduce((s, v) => s + (v.share_count || 0), 0);
        const totalCollects = videos.reduce((s, v) => s + (v.collect_count || 0), 0);
        const fc = Number(account.follower_count) || 0;
        const engagementRate = fc > 0 && videos.length > 0 ? parseFloat(((totalLikes + totalComments + totalShares) / videos.length / fc * 100).toFixed(3)) : null;
        setProfileData(prev => ({ ...prev, [accountId]: {
          profile: dbProfile, videos,
          stats: { total_videos: videos.length, total_plays: totalPlays, total_likes: totalLikes, total_comments: totalComments, total_shares: totalShares, total_collects: totalCollects, engagement_rate: engagementRate },
          growth, dailyStats, source: 'db'
        } }));
      }
    } catch (_) {}
    finally { setProfileLoading(prev => ({ ...prev, [accountId]: false })); }
  };

  /* ── Computed ──────────────────────────────────────────────────── */
  const selectedAccount = accounts.find(a => a.id === selectedId);
  const pd = selectedId ? profileData[selectedId] : null;
  const isLoadingPreview = selectedId ? !!profileLoading[selectedId] : false;
  const profile = pd?.profile;
  const videos = pd?.videos || [];
  const stats = pd?.stats;
  const growth = pd?.growth;
  const dailyStats = pd?.dailyStats || [];

  const grade = useMemo(() => getEngagementGrade(stats?.engagement_rate), [stats]);
  const frequency = useMemo(() => getPostingFrequency(videos), [videos]);
  const postingHours = useMemo(() => getBestPostingHours(videos), [videos]);
  const topVideos = useMemo(() => getTopVideos(videos), [videos]);
  const dailyChanges = useMemo(() => computeDailyChanges(dailyStats), [dailyStats]);
  const bestHours = useMemo(() => [...postingHours].filter(h => h.count > 0).sort((a, b) => b.avgEng - a.avgEng).slice(0, 3), [postingHours]);
  const chartData = useMemo(() => {
    if (!dailyStats || dailyStats.length < 2) return [];
    return [...dailyStats].sort((a, b) => new Date(a.stat_date) - new Date(b.stat_date)).map(s => ({ date: s.stat_date, value: Number(s.follower_count) || 0 }));
  }, [dailyStats]);

  /* ── Render ────────────────────────────────────────────────────── */
  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">

        {/* ═══ Account List ═══ */}
        <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TikTokIcon className="w-8 h-8 text-slate-900" />
              <div>
                <span className="text-base font-semibold text-slate-900">TikTok Radar</span>
                <div className="text-[11px] text-slate-500">Track & analyze TikTok accounts</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search..." className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-slate-300 outline-none transition-colors w-40" />
              <button className="px-3 py-1.5 text-sm rounded-lg bg-slate-900 text-white font-medium hover:bg-slate-800 transition-colors" onClick={() => { setIsAddOpen(true); setErrorMsg(''); setSearchKeyword(''); setSearchResults([]); }}>+ Add</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '30%' }}>Account</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '12%' }}>Videos</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '15%' }}>Followers</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '15%' }}>Likes</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '14%' }}>Eng. Rate</th>
                  <th className="px-4 py-2.5" style={{ width: '5%' }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400">No accounts yet. Click "+ Add" to start tracking.</td></tr>}
                {filtered.map((account) => {
                  const isSelected = selectedId === account.id;
                  return (
                    <tr key={account.id} className={`cursor-pointer transition-colors border-b border-slate-50 last:border-0 ${isSelected ? 'bg-slate-50' : 'hover:bg-slate-50/60'}`} onClick={() => selectAccount(account)}>
                      <td className="px-4 py-2.5 text-sm align-middle">
                        <div className="flex items-center gap-3">
                          {account.avatar_url ? (
                            <img src={account.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover ring-1 ring-slate-200" onError={e => { e.target.style.display = 'none'; }} />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-xs font-semibold">{(account.unique_id || '?')[0].toUpperCase()}</div>
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-medium truncate text-slate-900">{account.nickname || account.unique_id}</span>
                              {account.is_verified && <svg className="w-3.5 h-3.5 text-sky-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" /></svg>}
                            </div>
                            <div className="text-[11px] text-slate-400 truncate">@{account.unique_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-right font-medium tabular-nums text-slate-700">{format(account.video_count)}</td>
                      <td className="px-4 py-2.5 text-sm text-right font-medium tabular-nums text-slate-700">{format(account.follower_count)}</td>
                      <td className="px-4 py-2.5 text-sm text-right font-medium tabular-nums text-slate-700">{formatNumber(account.heart_count)}</td>
                      <td className="px-4 py-2.5 text-sm text-right">
                        {account.engagement_rate != null ? (
                          <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">{account.engagement_rate}%</span>
                        ) : <span className="text-slate-300">-</span>}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-right">
                        <button className="inline-flex p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors" title="Remove" onClick={(e) => { e.stopPropagation(); requestDelete(account); }}><Trash2 className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ═══ Dashboard ═══ */}
        {selectedId && pd && (
          <div ref={previewRef} className="mt-5 space-y-4">

            {/* Hero Card */}
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-6 py-5">
                <div className="flex flex-col md:flex-row items-start gap-5">
                  <div className="shrink-0">
                    {selectedAccount?.avatar_url ? (
                      <img src={selectedAccount.avatar_url} alt="" className="w-20 h-20 rounded-2xl object-cover ring-1 ring-slate-200" onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-500 text-2xl font-bold">{(selectedAccount?.unique_id || '?')[0].toUpperCase()}</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-lg font-bold text-slate-900">{profile?.nickname || profile?.unique_id}</span>
                      {profile?.is_verified && <svg className="w-4 h-4 text-sky-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" /></svg>}
                      {isLoadingPreview && <span className="text-xs text-slate-400 animate-pulse">Loading...</span>}
                    </div>
                    <a href={`https://tiktok.com/@${profile?.unique_id}`} target="_blank" rel="noreferrer" className="text-sm text-slate-500 hover:text-slate-700 hover:underline">@{profile?.unique_id}</a>
                    {profile?.signature && <p className="text-sm text-slate-600 mt-2 whitespace-pre-line line-clamp-2 leading-relaxed">{profile.signature}</p>}
                    {profile?.bio_link && <a href={profile.bio_link} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-600 hover:underline mt-1 inline-block">{profile.bio_link}</a>}
                  </div>
                  <div className="flex flex-col items-center shrink-0">
                    <div className={`w-14 h-14 rounded-xl ${grade.bg} border ${grade.border} flex items-center justify-center`}>
                      <span className={`text-xl font-black ${grade.color}`}>{grade.grade}</span>
                    </div>
                    <span className="text-[10px] text-slate-500 mt-1.5 font-medium">{grade.label}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
              <div className="rounded-xl border border-slate-200 p-3.5 bg-white">
                <div className="text-[11px] text-slate-500 mb-1">Followers</div>
                <div className="text-xl font-bold text-slate-900 tracking-tight">{formatNumber(profile?.follower_count)}</div>
                {growth?.followers?.weekly_change != null && growth.followers.weekly_change !== 0 && (
                  <div className={`text-[10px] font-medium mt-1 ${growth.followers.weekly_change > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {growth.followers.weekly_change > 0 ? '+' : ''}{formatNumber(growth.followers.weekly_change)} this week
                  </div>
                )}
              </div>
              <div className="rounded-xl border border-slate-200 p-3.5 bg-white">
                <div className="text-[11px] text-slate-500 mb-1">Following</div>
                <div className="text-xl font-bold text-slate-900 tracking-tight">{formatNumber(profile?.following_count)}</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3.5 bg-white">
                <div className="text-[11px] text-slate-500 mb-1">Videos</div>
                <div className="text-xl font-bold text-slate-900 tracking-tight">{formatNumber(profile?.video_count)}</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3.5 bg-white">
                <div className="text-[11px] text-slate-500 mb-1">Total Likes</div>
                <div className="text-xl font-bold text-slate-900 tracking-tight">{formatNumber(profile?.heart_count)}</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3.5 bg-white">
                <div className="text-[11px] text-slate-500 mb-1">Engagement Rate</div>
                <div className="text-xl font-bold text-slate-900 tracking-tight">{stats?.engagement_rate != null ? `${stats.engagement_rate}%` : '-'}</div>
                <div className={`text-[10px] font-medium mt-1 ${grade.color}`}>{grade.label}</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3.5 bg-white">
                <div className="text-[11px] text-slate-500 mb-1">Post Frequency</div>
                <div className="text-xl font-bold text-slate-900 tracking-tight">{frequency.perWeek || '-'}<span className="text-sm font-normal text-slate-400">/wk</span></div>
                <div className="text-[10px] text-slate-400 mt-1">{frequency.perMonth}/month</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3.5 bg-white">
                <div className="text-[11px] text-slate-500 mb-1">Avg. Views</div>
                <div className="text-xl font-bold text-slate-900 tracking-tight">{videos.length > 0 ? formatNumber(Math.round(videos.reduce((s, v) => s + (v.play_count || 0), 0) / videos.length)) : '-'}</div>
                <div className="text-[10px] text-slate-400 mt-1">per video (synced)</div>
              </div>
            </div>

            {/* Growth Chart */}
            {chartData.length >= 2 && (
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <SectionTitle>Follower Growth</SectionTitle>
                <div className="flex items-end justify-between mb-2 text-[10px] text-slate-400">
                  <span>{formatShortDate(chartData[0]?.date)}</span>
                  <span>{formatShortDate(chartData[chartData.length - 1]?.date)}</span>
                </div>
                <MiniLineChart data={chartData} height={100} color="#0ea5e9" />
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                  <span className="text-[11px] text-slate-400">{chartData.length} days of data</span>
                  {chartData.length >= 2 && (() => {
                    const change = chartData[chartData.length - 1].value - chartData[0].value;
                    return <span className={`text-xs font-semibold ${change >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{change >= 0 ? '+' : ''}{formatNumber(change)} total</span>;
                  })()}
                </div>
              </div>
            )}

            {/* Posting Analysis */}
            {videos.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <SectionTitle>Posting Analysis</SectionTitle>
                <PostingHoursChart hours={postingHours} />
                <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Weekly</span>
                    <span className="font-medium text-slate-800">{frequency.perWeek} videos/week</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Monthly</span>
                    <span className="font-medium text-slate-800">{frequency.perMonth} videos/month</span>
                  </div>
                  {bestHours.length > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Best Hours</span>
                      <span className="font-medium text-slate-800">{bestHours.map(h => `${String(h.hour).padStart(2, '0')}:00`).join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Daily Stats Table */}
            {dailyChanges.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="px-5 pt-5 pb-3"><SectionTitle>Daily Statistics</SectionTitle></div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="border-y border-slate-100">
                        <th className="px-4 py-2 text-left font-medium text-slate-500">Date</th>
                        <th className="px-4 py-2 text-right font-medium text-slate-500">Followers</th>
                        <th className="px-4 py-2 text-right font-medium text-slate-500">Change</th>
                        <th className="px-4 py-2 text-right font-medium text-slate-500">Videos</th>
                        <th className="px-4 py-2 text-right font-medium text-slate-500">Total Likes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(showAllDays ? dailyChanges : dailyChanges.slice(0, 14)).map((day) => (
                        <tr key={day.date} className="border-t border-slate-50 hover:bg-slate-50/50">
                          <td className="px-4 py-2 font-medium text-slate-700">{formatShortDate(day.date)}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-slate-700">{format(day.followers)}</td>
                          <td className="px-4 py-2 text-right">
                            {day.followerChange !== 0 ? (
                              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-semibold ${day.followerChange > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                                {day.followerChange > 0 ? '+' : ''}{format(day.followerChange)}
                              </span>
                            ) : <span className="text-slate-300">-</span>}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums text-slate-500">{format(day.videos)}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-slate-500">{formatNumber(day.hearts)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {dailyChanges.length > 14 && (
                  <div className="px-5 py-2.5 border-t border-slate-100 text-center">
                    <button onClick={() => setShowAllDays(!showAllDays)} className="text-xs font-medium text-sky-600 hover:text-sky-700">
                      {showAllDays ? 'Show less' : `Show all ${dailyChanges.length} days`}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Top Performing Videos */}
            {topVideos.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <SectionTitle>Top Performing Videos</SectionTitle>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {topVideos.map((video, i) => (
                    <button key={video.tiktok_video_id || i} onClick={() => setVideoPopup(video)} className="flex-shrink-0 w-36 group text-left">
                      <div className="relative aspect-[9/16] rounded-lg overflow-hidden bg-slate-100 mb-1.5">
                        {video.cover_url ? <img src={video.cover_url} alt="" className="w-full h-full object-cover" loading="lazy" onError={e => { e.target.style.display = 'none'; }} /> : null}
                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-white/90 text-slate-700">#{i + 1}</div>
                        <svg className="absolute top-1.5 right-1.5 w-4 h-4 text-white drop-shadow" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-slate-600">
                        <span>&#9654; {formatNumber(video.play_count)}</span>
                        <span>&#9829; {formatNumber(video.like_count)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-slate-500">
                        <span>&#128172; {formatNumber(video.comment_count)}</span>
                        <span>&#8599; {formatNumber(video.share_count)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* All Videos */}
            {videos.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="px-5 pt-5 pb-3 flex items-center justify-between">
                  <SectionTitle>All Videos ({videos.length})</SectionTitle>
                  <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
                    <button onClick={() => setViewMode('grid')} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}>Grid</button>
                    <button onClick={() => setViewMode('table')} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}>Table</button>
                  </div>
                </div>
                {viewMode === 'grid' && (
                  <div className="px-5 pb-5">
                    <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1.5">
                      {videos.map((v, i) => (
                        <button key={v.tiktok_video_id || i} onClick={() => setVideoPopup(v)} className="relative aspect-[9/16] bg-slate-100 rounded-lg overflow-hidden group block text-left">
                          {v.cover_url ? <img src={v.cover_url} alt="" className="w-full h-full object-cover" loading="lazy" onError={e => { e.target.style.display = 'none'; }} /> : null}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center px-1">
                            <div className="flex flex-col items-center gap-1 text-white text-[10px] font-bold text-center leading-tight">
                              <span>&#9654; {formatNumber(v.play_count)}</span>
                              <span>&#9829; {formatNumber(v.like_count)}</span>
                              <span>&#128172; {formatNumber(v.comment_count)}</span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {viewMode === 'table' && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="border-y border-slate-100">
                          <th className="px-3 py-2 text-left font-medium text-slate-500">#</th>
                          <th className="px-3 py-2 text-center font-medium text-slate-500 w-12">Cover</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-500 min-w-[220px]">Caption</th>
                          <th className="px-3 py-2 text-right font-medium text-slate-500">Views</th>
                          <th className="px-3 py-2 text-right font-medium text-slate-500">Likes</th>
                          <th className="px-3 py-2 text-right font-medium text-slate-500">Comments</th>
                          <th className="px-3 py-2 text-right font-medium text-slate-500">Shares</th>
                          <th className="px-3 py-2 text-right font-medium text-slate-500">Duration</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-500">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {videos.map((v, i) => (
                          <tr key={v.tiktok_video_id || i} className="border-t border-slate-50 hover:bg-slate-50/60 cursor-pointer" onClick={() => setVideoPopup(v)}>
                            <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                            <td className="px-3 py-2">{v.cover_url ? <img src={v.cover_url} alt="" className="w-10 h-14 rounded object-cover" loading="lazy" onError={e => { e.target.style.display = 'none'; }} /> : <div className="w-10 h-14 rounded bg-slate-100" />}</td>
                            <td className="px-3 py-2 text-slate-700 max-w-[280px]"><div className="truncate" title={v.caption}>{v.caption || '-'}</div></td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatNumber(v.play_count)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatNumber(v.like_count)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatNumber(v.comment_count)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatNumber(v.share_count)}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-500">{formatDuration(v.video_duration)}</td>
                            <td className="px-3 py-2 text-slate-600 whitespace-nowrap"><div>{formatDate(v.posted_at)}</div><div className="text-[10px] text-slate-400">{timeAgo(v.posted_at)}</div></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {videos.length === 0 && !isLoadingPreview && (
              <div className="rounded-xl border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-400">
                No videos stored yet. Videos will be synced automatically tonight.
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══ Video Popup ═══ */}
      {videoPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setVideoPopup(null)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <TikTokIcon className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-semibold text-slate-900">Video Detail</span>
              </div>
              <button className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" onClick={() => setVideoPopup(null)}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex flex-col md:flex-row">
              <div className="md:w-1/2 bg-slate-950 flex items-center justify-center min-h-[300px]">
                {videoPopup.video_url ? (
                  <video src={videoPopup.video_url} poster={videoPopup.cover_url || undefined} controls autoPlay playsInline className="w-full h-full object-contain max-h-[450px]" />
                ) : videoPopup.cover_url ? (
                  <img src={videoPopup.cover_url} alt="" className="w-full h-full object-contain max-h-[450px]" onError={e => { e.target.style.display = 'none'; }} />
                ) : <div className="text-slate-500 text-sm">No preview</div>}
              </div>
              <div className="md:w-1/2 p-5 flex flex-col">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-4">
                  <div className="rounded-lg bg-slate-50 p-3 text-center">
                    <div className="text-lg font-bold text-slate-900">{formatNumber(videoPopup.play_count)}</div>
                    <div className="text-[10px] text-slate-500">Views</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3 text-center">
                    <div className="text-lg font-bold text-slate-900">{formatNumber(videoPopup.like_count)}</div>
                    <div className="text-[10px] text-slate-500">Likes</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3 text-center">
                    <div className="text-lg font-bold text-slate-900">{formatNumber(videoPopup.comment_count)}</div>
                    <div className="text-[10px] text-slate-500">Comments</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3 text-center">
                    <div className="text-lg font-bold text-slate-900">{formatNumber(videoPopup.share_count)}</div>
                    <div className="text-[10px] text-slate-500">Shares</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3 text-center">
                    <div className="text-lg font-bold text-slate-900">{formatNumber(videoPopup.collect_count)}</div>
                    <div className="text-[10px] text-slate-500">Saves</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3 text-center">
                    <div className="text-lg font-bold text-slate-900">{formatDuration(videoPopup.video_duration)}</div>
                    <div className="text-[10px] text-slate-500">Duration</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                  <span>{formatDate(videoPopup.posted_at)}</span>
                  <span>{timeAgo(videoPopup.posted_at)}</span>
                </div>
                {videoPopup.music_title && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-3">
                    <span>&#9835;</span>
                    <span>{videoPopup.music_title}{videoPopup.music_author ? ` - ${videoPopup.music_author}` : ''}</span>
                  </div>
                )}
                {videoPopup.hashtags && videoPopup.hashtags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {videoPopup.hashtags.map((tag, i) => (
                      <span key={i} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">#{tag}</span>
                    ))}
                  </div>
                )}
                {videoPopup.caption && <div className="flex-1 overflow-y-auto mb-4"><p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{videoPopup.caption}</p></div>}
                <a href={`https://www.tiktok.com/@${selectedAccount?.unique_id || ''}/video/${videoPopup.tiktok_video_id}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors">
                  <TikTokIcon className="w-4 h-4" /> Open on TikTok
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Add Modal ═══ */}
      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setIsAddOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-lg mx-4">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">Add TikTok Account</h3>
              <button className="text-slate-400 hover:text-slate-600 transition-colors" onClick={() => setIsAddOpen(false)}>&#10005;</button>
            </div>
            <div className="p-5 space-y-3">
              <input
                value={searchKeyword}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchKeyword.trim() && addAccount(searchKeyword.trim())}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:border-slate-300 outline-none transition-colors"
                placeholder="Search TikTok users or enter @username"
                autoFocus
              />
              {errorMsg ? <div className="text-xs text-red-600">{errorMsg}</div> : null}
              {isSearching && <div className="text-xs text-slate-400 text-center py-2 animate-pulse">Searching...</div>}

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="max-h-64 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-50">
                  {searchResults.map((user, i) => (
                    <button
                      key={user.unique_id || i}
                      onClick={() => addAccount(user.unique_id)}
                      disabled={isSaving}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left disabled:opacity-50"
                    >
                      {user.avatar_url ? (
                        <img src={user.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover ring-1 ring-slate-200" onError={e => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 text-xs font-semibold">{(user.unique_id || '?')[0].toUpperCase()}</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-slate-900 truncate">{user.nickname || user.unique_id}</span>
                          {user.is_verified && <svg className="w-3.5 h-3.5 text-sky-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" /></svg>}
                        </div>
                        <div className="text-[11px] text-slate-400">@{user.unique_id} · {formatNumber(user.follower_count)} followers</div>
                      </div>
                      <span className="text-xs text-slate-400 shrink-0">{isSaving ? 'Adding...' : '+ Add'}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Direct add button when no search results */}
              {searchResults.length === 0 && searchKeyword.trim().length >= 2 && !isSearching && (
                <div className="flex justify-end gap-2 pt-2">
                  <button className="px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50 transition-colors" onClick={() => setIsAddOpen(false)}>Cancel</button>
                  <button className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" onClick={() => addAccount(searchKeyword.trim())} disabled={isSaving || !searchKeyword.trim() || !getBrandId()}>{isSaving ? 'Adding...' : 'Add Account'}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ Delete Modal ═══ */}
      {deleteState.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteState({ open: false, step: 1, account: null })} />
          <div className="relative bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md mx-4 p-5">
            <div className="text-base font-semibold text-slate-900 mb-2">Remove Account</div>
            {deleteState.step === 1 ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">You're about to remove "@{deleteState.account?.unique_id}" from this brand. Continue?</p>
                <div className="flex justify-end gap-2 pt-2">
                  <button className="px-3 py-2 rounded-lg border border-slate-200 text-sm" onClick={() => setDeleteState({ open: false, step: 1, account: null })}>Cancel</button>
                  <button className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium" onClick={() => setDeleteState(s => ({ ...s, step: 2 }))}>Continue</button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Are you sure? This won't delete the account from the library — it only removes access for this brand.</p>
                {errorMsg ? <div className="text-xs text-red-600">{errorMsg}</div> : null}
                <div className="flex justify-end gap-2 pt-2">
                  <button className="px-3 py-2 rounded-lg border border-slate-200 text-sm" onClick={() => setDeleteState({ open: false, step: 1, account: null })}>Cancel</button>
                  <button className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-medium disabled:opacity-50" disabled={isSaving} onClick={performDelete}>{isSaving ? 'Removing...' : 'Remove'}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
